import { BoltRounded } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useRunTestTopup, useTestTopupStatus } from "@/hooks/useCampaignTester";
import type { TopupSweepResult } from "@/interfaces/campaignTester";
import { formatDate, formatDateTime } from "@/utils/date-format";

/** TopupStatus — mirrors the API constant (constants/campaignCredits.ts). */
const ATTEMPT_STATUS: Record<
  number,
  { label: string; colour: "default" | "info" | "success" | "error" | "warning" }
> = {
  0: { label: "Pending", colour: "info" },
  1: { label: "Succeeded", colour: "success" },
  2: { label: "Failed", colour: "error" },
  3: { label: "Cancelled", colour: "default" },
  4: { label: "Blocked", colour: "warning" },
};

const th = {
  fontSize: 10.5,
  letterSpacing: "0.6px",
  textTransform: "uppercase" as const,
  color: "var(--color-graphite)",
  fontWeight: 700,
  whiteSpace: "nowrap" as const,
};

/** One of the three levels the model turns on. */
function Level({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <Box sx={{ minWidth: 132 }}>
      <Typography sx={th}>{label}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 600, lineHeight: 1.2 }}>{value}</Typography>
      <Typography sx={{ fontSize: 11, color: "var(--color-graphite)" }}>{hint}</Typography>
    </Box>
  );
}

/**
 * An automated campaign's standing against its maintenance margin, and the button that runs the
 * hourly sweep on demand.
 *
 * The three levels are shown side by side because the model is only legible as a relationship
 * between them: coverage is what is left to spend, the margin is the level that TRIGGERS a call,
 * and the minimum balance is what a call restores to. Reading any one alone tells you nothing —
 * "coverage 360" is healthy or a breach depending entirely on the number beside it.
 *
 * Renders nothing for a prepaid campaign (`configured: false`): there are no terms, so there is no
 * margin to be above or below, and a panel reading zeroes would imply a model this campaign does
 * not use.
 */
export interface TopupPanelProps {
  campaignId: number;
}

export default function TopupPanel({ campaignId }: TopupPanelProps) {
  const { data: status, isLoading } = useTestTopupStatus(campaignId);
  const run = useRunTestTopup(campaignId);
  const [lastRun, setLastRun] = useState<TopupSweepResult | null>(null);

  if (isLoading) {
    return (
      <Box sx={{ py: 3, textAlign: "center" }}>
        <CircularProgress aria-label="Loading" size={20} />
      </Box>
    );
  }
  // Prepaid and postpaid campaigns have no terms row — nothing to report, so nothing is rendered.
  if (!status?.configured) return null;

  async function sweep(opts: { dryRun?: boolean; decline?: string }) {
    setLastRun(await run.mutateAsync(opts));
  }

  const money = (v: string | null) => (v == null ? "—" : `${Number(v).toLocaleString()} cr`);

  /**
   * Why the sweep did not even look at this campaign. The same conditions its due-query filters on,
   * in the order that query applies them, so the sentence cannot claim a different reason from the
   * one that actually excluded it.
   */
  const skipReason = status.campaignClosed
    ? "the campaign is closed"
    : !status.enabled
      ? "auto-charging is paused on this campaign"
      : status.onHold
        ? "the budget is on hold"
        : status.failureCount > 0 && status.nextAttemptAt == null
          ? "the tries are spent — nominate a working card or retry by hand"
          : status.nextAttemptAt && new Date(status.nextAttemptAt) > new Date()
            ? `backing off until ${formatDateTime(status.nextAttemptAt)}`
            : null;

  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Automated top-up</Typography>
        {status.belowMargin ? (
          <Chip size="small" color="warning" label="Below maintenance margin" />
        ) : (
          <Chip size="small" color="success" label="Above margin" />
        )}
        {status.onHold && <Chip size="small" color="error" label="On hold" />}
        {!status.enabled && <Chip size="small" label="Auto-charge paused" />}
        {status.campaignClosed && <Chip size="small" label="Campaign closed" />}
        {status.launchedBeforePayment && (
          <Chip size="small" color="info" label="Launched before payment" />
        )}
      </Stack>

      <Stack direction="row" flexWrap="wrap" gap={3} sx={{ mb: 2 }}>
        {/* The two models show DIFFERENT levels, because they mean different things. Showing the
            step model's labels over a campaign funded by restore-to-deposit — or the reverse —
            would be the same lie the live arithmetic goes out of its way to avoid. */}
        {status.stepModel ? (
          <>
            <Level
              label="Approved budget"
              value={money(status.approvedBudget)}
              hint="Step 4 total — what a step is a % of"
            />
            <Level label="Obligation" value={money(status.obligation)} hint="still unredeemed" />
            <Level
              label="Trigger"
              value={money(status.margin)}
              hint="% of obligation, floored at 2 gifts"
            />
            <Level label="Coverage" value={money(status.coverage)} hint="left to spend now" />
            <Level label="Next step" value={money(status.nextCharge)} hint="what one call adds" />
          </>
        ) : (
          <>
            <Level label="Obligation" value={money(status.obligation)} hint="still unredeemed" />
            {/* Both, because the gap between them IS the cap doing its job: once the obligation
                falls below the fixed deposit, the target follows it down and the campaign ends
                exactly funded rather than over-funded. */}
            <Level
              label="Deposit (fixed)"
              value={money(status.minDepositAmount)}
              hint="set at launch, never moves"
            />
            <Level
              label="Restore target"
              value={money(status.minimumBalance)}
              hint="deposit, capped at obligation"
            />
            <Level label="Margin" value={money(status.margin)} hint="a call triggers below this" />
            <Level label="Coverage" value={money(status.coverage)} hint="left to spend now" />
          </>
        )}
        {/* The two halves of the NEXT call, side by side, because the step has two backers and a
            single figure cannot say which. The panel used to show only this one under a label that
            read like the charge, so a 200 step funded 150 from the pool looked like a 200 charge. */}
        <Level
          label="Card would buy"
          value={money(status.shortfall)}
          hint="the rest of the step comes from the pool"
        />
        <Level
          label="Pool would cover"
          value={money(String(Math.max(Number(status.nextCharge) - Number(status.shortfall), 0)))}
          hint="credits the group already holds"
        />
      </Stack>

      {!status.stepModel && status.configured && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This campaign was opened before the step model, so it keeps the terms it launched under:
          the trigger is a percentage of its fixed deposit, and a call restores coverage to that
          deposit rather than adding a step. Seed a new campaign to exercise the current model.
        </Alert>
      )}

      {!status.hasPaymentMethod && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No card nominated, so every call fails closed and is recorded as blocked. Seed the
          campaign against a group with a default card to exercise the charge.
        </Alert>
      )}
      {status.failureCount > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {status.failureCount} consecutive failure{status.failureCount === 1 ? "" : "s"}
          {/* The TIME, not just the date: the backoff is five minutes, and a date alone makes that
              indistinguishable from tomorrow — which reads as "the sweep is broken". */}
          {status.nextAttemptAt
            ? ` — next attempt after ${formatDateTime(status.nextAttemptAt)}.`
            : ". The tries are spent: the sweep will not pick this campaign up again until a working card is nominated or someone retries by hand."}
        </Alert>
      )}

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        {/* The real cron, scoped to this campaign. Dry run prices and RECORDS the decision without
            charging, which is the safe way to see whether a call is due; the live run charges the
            nominated card and settles through the same path the Stripe webhook uses. */}
        <Button
          variant="contained"
          startIcon={<BoltRounded />}
          disabled={run.isPending}
          onClick={() => void sweep({})}
          sx={{ bgcolor: "var(--color-dark-dust)" }}
        >
          Run top-up sweep
        </Button>
        <Button
          variant="outlined"
          disabled={run.isPending}
          onClick={() => void sweep({ dryRun: true })}
        >
          Dry run
        </Button>
        {/* The card fails. Everything after the throw is the real failure path — the attempt is
            marked, the streak counted, the backoff set — so the recovery a client walks (a new card
            clears the count and retries on the spot) can be driven from here rather than only
            asserted in a suite. */}
        <Button
          variant="outlined"
          color="error"
          disabled={run.isPending}
          onClick={() => void sweep({ decline: "card_declined" })}
        >
          Decline the card
        </Button>
        {run.isPending && <CircularProgress aria-label="Loading" size={18} />}
        {lastRun && !run.isPending && (
          <Typography sx={{ fontSize: 12.5, color: "var(--color-graphite)" }}>
            {lastRun.checked} checked · {lastRun.called} called · {lastRun.restored} restored ·{" "}
            {lastRun.blocked} blocked
            {/* `0 checked` means the campaign was not in the sweep's population AT ALL, which is a
                different fact from "nothing was owed" — and there are four reasons for it. Leaving
                the reader to guess is what made a five-minute backoff look like a broken button. */}
            {lastRun.checked === 0 && skipReason ? ` — ${skipReason}` : ""}
          </Typography>
        )}
      </Stack>

      {run.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(run.error as Error).message}
        </Alert>
      )}

      {/* Every top-up this campaign has had — `credit_topup`, complete. Not a log of SWEEPS: a
          sweep that finds the campaign above its margin produces no top-up, so there is nothing
          here for it to record. The two are different questions and the table answers the first. */}
      <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.75 }}>Top-up history</Typography>
      {status.attempts.length === 0 ? (
        <Typography sx={{ fontSize: 12.5, color: "var(--color-graphite)", mb: 1 }}>
          No top-ups yet — no call has been due on this campaign. Sweeps that find it above its
          margin produce nothing to record here.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={th}>#</TableCell>
              <TableCell sx={th}>Status</TableCell>
              <TableCell sx={th}>Trigger</TableCell>
              <TableCell sx={th} align="right">
                Amount
              </TableCell>
              <TableCell sx={th}>Purchase</TableCell>
              <TableCell sx={th}>Raised</TableCell>
              <TableCell sx={th}>Settled</TableCell>
              <TableCell sx={th}>Detail</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {status.attempts.map((a) => {
              const s = ATTEMPT_STATUS[a.status] ?? { label: String(a.status), colour: "default" };
              return (
                <TableRow key={a.id}>
                  <TableCell>{a.id}</TableCell>
                  <TableCell>
                    <Chip size="small" color={s.colour} label={s.label} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>{a.trigger ?? "—"}</TableCell>
                  <TableCell align="right">{money(a.amount)}</TableCell>
                  <TableCell>{a.purchaseId ?? "—"}</TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>{formatDate(a.createdAt)}</TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>
                    {a.completedAt ? formatDate(a.completedAt) : "—"}
                  </TableCell>
                  {/* The refusal reason IS the point of a blocked row — "why did this campaign run
                      dry" is the question the attempt log exists to answer. */}
                  <TableCell sx={{ fontSize: 12.5, color: "var(--color-graphite)" }}>
                    {a.failureCode === "dry_run"
                      ? "Dry run — priced and recorded, card not charged"
                      : (a.failureMessage ?? a.failureCode ?? "—")}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
